import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProperties, createProperty, updateProperty, deleteProperty, getUnits, createUnit, updateUnit, deleteUnit, getDoorCodes, getMarketingLinks } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Plus, DoorOpen, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { PropertyFormDialog } from '@/components/properties/PropertyFormDialog';
import { UnitFormDialog } from '@/components/properties/UnitFormDialog';
import { PropertyCreatePanel } from '@/components/properties/PropertyCreatePanel';
import { UnitCreatePanel } from '@/components/properties/UnitCreatePanel';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { DoorCodeEditDialog } from '@/components/properties/DoorCodeEditDialog';
import { MarketingLinkEditDialog } from '@/components/properties/MarketingLinkEditDialog';

const emptyPropertyForm = {
  name: '', address: '', owner_manager_name: '', owner_manager_phone: '',
  owner_manager_email: '', available_parking: '', pets_permitted: false,
  pet_notes: '', building_amenities: [], additional_notes: '', building_id: '',
};

const emptyUnitForm = {
  property_id: '', unit_number: '', unit_size: '', unit_size_custom: '',
  base_rent: '', additional_monthly_costs: [], availability_start_date: '', close_date: '',
  filter_size: '', filter_last_changed: ''
};

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [unitsByProperty, setUnitsByProperty] = useState({});
  const [codesByUnit, setCodesByUnit] = useState({});
  const [linksByUnit, setLinksByUnit] = useState({});
  const [loading, setLoading] = useState(true);

  const [showCreatePropPanel, setShowCreatePropPanel] = useState(false);
  const [propDialogOpen, setPropDialogOpen] = useState(false);
  const [editingProp, setEditingProp] = useState(null);
  const [propForm, setPropForm] = useState(emptyPropertyForm);
  const [savingProp, setSavingProp] = useState(false);

  const [showCreateUnitPanel, setShowCreateUnitPanel] = useState(false);
  const [createUnitPropId, setCreateUnitPropId] = useState(null);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState(emptyUnitForm);
  const [savingUnit, setSavingUnit] = useState(false);

  const [expandedDetails, setExpandedDetails] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});

  // Inline edit dialogs for door codes and marketing links
  const [doorCodeDialog, setDoorCodeDialog] = useState({ open: false, unit: null });
  const [marketingDialog, setMarketingDialog] = useState({ open: false, unit: null });

  const openEditDoorCodes = (unit) => setDoorCodeDialog({ open: true, unit });
  const openEditMarketing = (unit) => setMarketingDialog({ open: true, unit });

  const fetchData = async () => {
    try {
      const [props, units, codes, links] = await Promise.all([
        getProperties(), getUnits(), getDoorCodes(), getMarketingLinks()
      ]);
      setProperties(props);
      const grouped = {};
      units.forEach(u => {
        if (!grouped[u.property_id]) grouped[u.property_id] = [];
        grouped[u.property_id].push(u);
      });
      setUnitsByProperty(grouped);
      const cMap = {};
      codes.forEach(c => { cMap[c.unit_id] = c; });
      setCodesByUnit(cMap);
      const lMap = {};
      links.forEach(l => { lMap[l.unit_id] = l; });
      setLinksByUnit(lMap);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const unitMap = useMemo(() => {
    const m = {};
    Object.values(unitsByProperty).forEach(arr => arr.forEach(u => { m[u.id] = u; }));
    return m;
  }, [unitsByProperty]);

  // ---- Property CRUD ----
  const openCreateProp = () => {
    setShowCreatePropPanel(true);
  };

  const openEditProp = (prop) => {
    setEditingProp(prop);
    setPropForm({
      name: prop.name || '', address: prop.address || '',
      owner_manager_name: prop.owner_manager_name || '',
      owner_manager_phone: prop.owner_manager_phone || '',
      owner_manager_email: prop.owner_manager_email || '',
      available_parking: prop.available_parking || '',
      pets_permitted: prop.pets_permitted || false,
      pet_notes: prop.pet_notes || '',
      building_amenities: prop.building_amenities || [],
      additional_notes: prop.additional_notes || '',
      building_id: prop.building_id != null ? prop.building_id : ''
    });
    setPropDialogOpen(true);
  };

  const handleSaveProp = async () => {
    if (!propForm.name || !propForm.address) {
      toast.error('Name and address are required');
      return;
    }
    setSavingProp(true);
    const payload = {
      ...propForm,
      building_id: propForm.building_id !== '' ? parseInt(propForm.building_id, 10) : null
    };
    try {
      if (editingProp) {
        await updateProperty(editingProp.id, payload);
        toast.success('Property updated');
      } else {
        await createProperty(payload);
        toast.success('Property created');
      }
      setPropDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save property');
    } finally {
      setSavingProp(false);
    }
  };

  // Save just the property-level access items list (backup keys/codes).
  // We echo back the existing property fields to satisfy PropertyCreate's
  // required fields, then swap in the new access_items array.
  const savePropertyAccess = async (prop, newAccessItems) => {
    const payload = {
      name: prop.name,
      address: prop.address,
      owner_manager_name: prop.owner_manager_name || '',
      owner_manager_phone: prop.owner_manager_phone || '',
      owner_manager_email: prop.owner_manager_email || '',
      available_parking: prop.available_parking || '',
      pets_permitted: !!prop.pets_permitted,
      pet_notes: prop.pet_notes || '',
      building_amenities: prop.building_amenities || [],
      additional_notes: prop.additional_notes || '',
      building_id: prop.building_id != null ? prop.building_id : null,
      marlins_decal_property: !!prop.marlins_decal_property,
      access_items: newAccessItems,
    };
    try {
      await updateProperty(prop.id, payload);
      toast.success('Property access updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteProp = async (id) => {
    if (!window.confirm('Delete this property? All units must be removed first.')) return;
    try {
      await deleteProperty(id);
      toast.success('Property deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete property');
    }
  };

  // ---- Unit CRUD ----
  const openCreateUnit = (propertyId) => {
    setCreateUnitPropId(propertyId);
    setShowCreateUnitPanel(true);
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({
      property_id: unit.property_id || '',
      unit_number: unit.unit_number || '',
      unit_size: unit.unit_size || '',
      unit_size_custom: unit.unit_size_custom || '',
      base_rent: unit.base_rent || '',
      additional_monthly_costs: unit.additional_monthly_costs || [],
      availability_start_date: unit.availability_start_date || '',
      close_date: unit.close_date || '',
      filter_size: unit.filter_size || '',
      filter_last_changed: unit.filter_last_changed || ''
    });
    setUnitDialogOpen(true);
  };

  const handleSaveUnit = async () => {
    if (!unitForm.property_id || !unitForm.unit_number || !unitForm.unit_size || !unitForm.base_rent || !unitForm.availability_start_date) {
      toast.error('Please fill all required fields');
      return;
    }
    setSavingUnit(true);
    const payload = {
      ...unitForm,
      base_rent: parseFloat(unitForm.base_rent),
      additional_monthly_costs: unitForm.additional_monthly_costs.filter(c => c.name && c.amount).map(c => ({ name: c.name, amount: parseFloat(c.amount) })),
      close_date: unitForm.close_date || null,
      filter_size: unitForm.filter_size || '',
      filter_last_changed: unitForm.filter_last_changed || null
    };
    try {
      if (editingUnit) {
        await updateUnit(editingUnit.id, payload);
        toast.success('Unit updated');
      } else {
        await createUnit(payload);
        toast.success('Unit created');
      }
      setUnitDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSavingUnit(false);
    }
  };

  const handleDeleteUnit = async (id) => {
    if (!window.confirm('Delete this unit? All tenants must be removed first.')) return;
    try {
      await deleteUnit(id);
      toast.success('Unit deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete unit');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Properties</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your buildings, units, and details</p>
        </div>
        <Button onClick={openCreateProp} data-testid="properties-create-button">
          <Plus className="h-4 w-4 mr-2" /> Add Property
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground">Loading...</div>
      ) : properties.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-semibold">No properties yet</h3>
            <p className="text-sm text-muted-foreground mt-1">Add your first property to get started</p>
            <Button className="mt-4" onClick={openCreateProp}>Add Property</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {[...properties].sort((a, b) => {
            const aId = a.building_id != null ? a.building_id : 99999;
            const bId = b.building_id != null ? b.building_id : 99999;
            if (aId !== bId) return aId - bId;
            return (a.name || '').localeCompare(b.name || '');
          }).map(prop => {
            const units = unitsByProperty[prop.id] || [];
            return (
              <PropertyCard
                key={prop.id}
                prop={prop}
                units={units}
                unitMap={unitMap}
                codesByUnit={codesByUnit}
                linksByUnit={linksByUnit}
                expanded={{
                  details: expandedDetails[prop.id],
                  units: expandedUnits[prop.id],
                }}
                toggles={{
                  details: () => setExpandedDetails(prev => ({ ...prev, [prop.id]: !prev[prop.id] })),
                  units: () => setExpandedUnits(prev => ({ ...prev, [prop.id]: !prev[prop.id] })),
                }}
                handlers={{
                  editProp: () => openEditProp(prop),
                  deleteProp: () => handleDeleteProp(prop.id),
                  createUnit: () => openCreateUnit(prop.id),
                  editUnit: openEditUnit,
                  deleteUnit: handleDeleteUnit,
                  editDoorCodes: openEditDoorCodes,
                  editMarketing: openEditMarketing,
                  saveAccessItems: (items) => savePropertyAccess(prop, items),
                }}
              />
            );
          })}
        </div>
      )}

      {/* Create panels (progressive side panel) */}
      <PropertyCreatePanel
        open={showCreatePropPanel}
        onClose={() => setShowCreatePropPanel(false)}
        onCreated={fetchData}
      />

      <UnitCreatePanel
        open={showCreateUnitPanel}
        onClose={() => { setShowCreateUnitPanel(false); setCreateUnitPropId(null); }}
        onCreated={fetchData}
        defaultPropertyId={createUnitPropId}
        properties={properties}
      />

      {/* Edit dialogs (keep existing pattern) */}
      <PropertyFormDialog
        open={propDialogOpen}
        onOpenChange={setPropDialogOpen}
        editing={editingProp}
        form={propForm}
        setForm={setPropForm}
        onSave={handleSaveProp}
        saving={savingProp}
      />

      <UnitFormDialog
        open={unitDialogOpen}
        onOpenChange={setUnitDialogOpen}
        editing={editingUnit}
        form={unitForm}
        setForm={setUnitForm}
        onSave={handleSaveUnit}
        saving={savingUnit}
      />

      {/* Inline Door Codes / Marketing dialogs (replace dedicated pages) */}
      <DoorCodeEditDialog
        open={doorCodeDialog.open}
        onOpenChange={(v) => setDoorCodeDialog(d => ({ ...d, open: v }))}
        unit={doorCodeDialog.unit}
        existing={doorCodeDialog.unit ? codesByUnit[doorCodeDialog.unit.id] : null}
        onSaved={fetchData}
      />
      <MarketingLinkEditDialog
        open={marketingDialog.open}
        onOpenChange={(v) => setMarketingDialog(d => ({ ...d, open: v }))}
        unit={marketingDialog.unit}
        existing={marketingDialog.unit ? linksByUnit[marketingDialog.unit.id] : null}
        onSaved={fetchData}
      />

      {/* Quick links to related pages (parity with Dashboard -> Features button) */}
      {!loading && properties.length > 0 && (
        <div className="flex justify-center gap-3 pt-2">
          <Button
            variant="ghost"
            className="text-[13px] text-gray-400 hover:text-gray-600 gap-2 font-medium"
            onClick={() => navigate('/door-codes')}
            data-testid="properties-door-codes-button"
          >
            <DoorOpen className="h-4 w-4" />
            Door Codes
          </Button>
          <Button
            variant="ghost"
            className="text-[13px] text-gray-400 hover:text-gray-600 gap-2 font-medium"
            onClick={() => navigate('/marketing')}
            data-testid="properties-marketing-button"
          >
            <Megaphone className="h-4 w-4" />
            Marketing
          </Button>
        </div>
      )}
    </div>
  );
}
